import { Flex, VStack } from '@chakra-ui/layout'
import type { ReactElement } from 'react'
import React, { useContext, useMemo, useState } from 'react'
import { MLViewType } from '../../../constants'
import { MLContext } from '../../../providers'
import AccessDeniedView from './AccessDeniedView'
import CollectionsGrid from './GridView/CollectionsGrid'
import CollectionsList from './ListView/CollectionsList'
import FilesGrid from './GridView/FilesGrid'
import FoldersGrid from './GridView/FoldersGrid'
import FilesList from './ListView/FilesList'
import FoldersList from './ListView/FoldersList'
import NoFilesOrFoldersView from './NoFilesOrFoldersView'
import NotFoundView from './NotFoundView'
import EmptyMediaCollectionView from './EmptyMediaCollectionView'
import NoMediaCollectionsView from './NoMediaCollectionsView'
import CollectionsGridLoader from './GridView/CollectionGridLoader'
import CollectionsListLoader from './ListView/CollectionListLoader'
import MLGridLoader from './GridView/MLGridLoader'
import MLListLoader from './ListView/MLListLoader'

const CollectionsView = (): ReactElement => {
  const [
    {
      mediaCollectionList,
      loading,
      loadingMediaCollections,
      view,
      currentMediaCollection,
      viewableParent,
      folderList,
      fileList,
      folderPath,
      loadingFiles,
      loadingFolders,
      loadingViewableParent,
    },
  ] = useContext(MLContext)

  const [loadComplete, setLoadComplete] = useState<{
    files: boolean
    folders: boolean
  }>({
    files: false,
    folders: false,
  })

  const setOnLoadComplete = (type: 'folder' | 'file'): void => {
    if (loadComplete.files && loadComplete.folders) {
      return
    }

    setLoadComplete(prev => {
      if (type === 'folder') {
        return {
          ...prev,
          folders: true,
        }
      }

      return {
        ...prev,
        files: true,
      }
    })
  }

  const childComponent = useMemo<React.ReactElement>(() => {
    if (
      loadingMediaCollections ||
      loading ||
      loadingFiles ||
      loadingFolders ||
      loadingViewableParent
    ) {
      return currentMediaCollection ? (
        view === MLViewType.GRID ? (
          <MLGridLoader />
        ) : (
          <MLListLoader />
        )
      ) : view === MLViewType.GRID ? (
        <CollectionsGridLoader />
      ) : (
        <CollectionsListLoader />
      )
    }

    if (viewableParent === 'NOT_ALLOWED') {
      return <AccessDeniedView />
    }

    if (viewableParent === 'NOT_FOUND') {
      return <NotFoundView />
    }

    if (
      mediaCollectionList.length === 0 &&
      !currentMediaCollection &&
      folderPath === '/'
    ) {
      return <NoMediaCollectionsView />
    }

    return (
      <VStack alignItems="flex-start" spacing={8}>
        {currentMediaCollection ? (
          folderList.length === 0 && fileList.length === 0 ? (
            folderPath === '/' ? (
              <EmptyMediaCollectionView />
            ) : (
              <NoFilesOrFoldersView />
            )
          ) : (
            <>
              {view === MLViewType.GRID ? (
                <FoldersGrid
                  setOnLoadComplete={setOnLoadComplete}
                  loadComplete={loadComplete}
                />
              ) : null}
              {view === MLViewType.LIST ? (
                <FoldersList
                  setOnLoadComplete={setOnLoadComplete}
                  loadComplete={loadComplete}
                />
              ) : null}
              {view === MLViewType.GRID ? (
                <FilesGrid
                  setOnLoadComplete={setOnLoadComplete}
                  loadComplete={loadComplete}
                />
              ) : null}
              {view === MLViewType.LIST ? (
                <FilesList
                  setOnLoadComplete={setOnLoadComplete}
                  loadComplete={loadComplete}
                />
              ) : null}
            </>
          )
        ) : (
          <>
            {view === MLViewType.GRID ? <CollectionsGrid /> : null}
            {view === MLViewType.LIST ? <CollectionsList /> : null}
          </>
        )}
      </VStack>
    )
  }, [
    loadingMediaCollections,
    view,
    currentMediaCollection,
    mediaCollectionList,
    loading,
    loadingFiles,
    loadingFolders,
    fileList,
    folderList,
    folderPath,
    viewableParent,
    loadingViewableParent,
    loadComplete.files,
    loadComplete.folders,
  ])

  return (
    <Flex height="100%" direction="column">
      {childComponent}
    </Flex>
  )
}

export default CollectionsView
